/**
 * MobileRefreshButton View
 *
 * Pure UI component for mobile refresh button without animation.
 * @remarks
 * Animations cause crashes on Android with NativeWind, so we use opacity
 * to indicate state instead.
 */

import { TEST_ID_AI_PLAYER_SUMMARY } from "e2e/testId";
import { RefreshCw } from "lucide-react-native";
import { memo } from "react";
// eslint-disable-next-line no-restricted-imports -- raw View required for RN pointerEvents responder API; Box atom's web div type lacks pointerEvents (rung E non-atomizable)
import { View } from "react-native";

import { Icon, type IconSize } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms";

/**
 * Props interface for MobileRefreshButton view component
 */
interface MobileRefreshButtonViewProps {
  isGenerating: boolean;
  onRefresh: () => void;
  iconSize: IconSize;
  iconColor: string;
  containerOpacity: number;
}

/**
 * View component for mobile refresh button
 * @param props - Component props
 * @param props.isGenerating - Whether AI summary generation is in progress
 * @param props.onRefresh - Callback fired when the refresh button is pressed
 * @param props.iconSize - IconSize token for the refresh icon
 * @param props.iconColor - Color of the refresh icon
 * @param props.containerOpacity - Opacity of the button container (0-1)
 */
const MobileRefreshButtonView = ({
  isGenerating,
  onRefresh,
  iconSize,
  iconColor,
  containerOpacity,
}: MobileRefreshButtonViewProps) => (
  <Pressable
    testID={TEST_ID_AI_PLAYER_SUMMARY.REFRESH_BUTTON}
    onPress={onRefresh}
    disabled={isGenerating}
    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
    // eslint-disable-next-line no-restricted-syntax -- data-driven containerOpacity (continuous 0-1 generation-state indicator); no closed opacity token
    UNSAFE_style={{ opacity: containerOpacity }}
  >
    <View pointerEvents="none">
      <Icon as={RefreshCw} size={iconSize} fill="none" stroke={iconColor} />
    </View>
  </Pressable>
);

MobileRefreshButtonView.displayName = "MobileRefreshButtonView";

export default memo(MobileRefreshButtonView);
